import React, { useEffect, useRef, useState } from 'react';
import {
  Upload,
  FileText,
  Trash2,
  GripVertical,
  FolderOpen,
  RotateCw,
  Info,
  Layers,
  ChevronRight,
  ChevronDown,
  Plus,
  AlertTriangle,
} from 'lucide-react';
import { updatedText, stamp, lastTouchedAt } from '../timeText';

/** Template cards shown before the rest collapse into a "+N more" — see TemplateStarters. */
const STARTER_LIMIT = 3;

/**
 * Chip text for a document that came from a template: the TEMPLATE's name and
 * the version copied ("Service Agreement v2"). The row's title is already the
 * PDF's file name, so repeating "from template" there says nothing — which
 * template, and which version of it, is the part that is not visible otherwise.
 * A template deleted since the copy is no longer in the list, so the generic
 * wording stays as the fallback.
 */
function templateChipText(doc, nameById) {
  const name = nameById.get(String(doc.sourceTemplateId));
  const version = doc.sourceTemplateVersion ? ` v${doc.sourceTemplateVersion}` : '';
  return name ? `${name}${version}` : `from template${version}`;
}

/**
 * Step 1 — Documents.
 *
 * Shared by the envelope builder and the template editor: both hold an ordered
 * list of PDFs, and both add them the same three ways. The differences are
 * props, not structure — `allowTemplates` is off in the template editor (a
 * template copying another template would blur which one a signature came
 * from), and the scope note differs.
 *
 * Order is the order a signer walks through them, which is why the rows are
 * numbered and re-orderable rather than sorted by name.
 *
 * Templates surface HERE rather than in the app header: a template is a
 * starting point for a request, so the only moment it is relevant is the moment
 * a starting point is being chosen. While the list is empty they get their own
 * section under the dropzone; once a document is in they collapse to the
 * "Add from template" button, because the choice has already been made.
 */
export default function Step1Documents({
  documents = [],
  /** ALL templates on the board — published ones are selectable, the rest are
   *  counted so the picker can explain why they are not. */
  templates = [],
  itemFiles = null,
  scopeNote,
  busy = false,
  allowTemplates = true,
  allowItemFiles = true,
  maxDocuments = 10,
  onUpload,
  onAddFromTemplate,
  onAddFromColumn,
  onRemove,
  onReorder,
  onRename,
  onManageTemplates,
  onEditTemplate,
  onCreateTemplate,
}) {
  // A template must be published before an envelope can copy from it — that is
  // what makes "copy the published version" meaningful. The drafts are kept, not
  // just counted, so the starters section can name the one you left unfinished.
  const publishedTemplates = templates
    .filter((t) => Number(t.version) > 0)
    // Most recently updated first: the template you last touched is the one you
    // are most likely sending now. Sorted here rather than left in arrival order
    // so the cards stay in step with the "Updated …" date each one shows.
    .sort((a, b) => stamp(lastTouchedAt(b)) - stamp(lastTouchedAt(a)));
  const unpublishedTemplates = templates.filter((t) => !(Number(t.version) > 0));
  const unpublishedCount = unpublishedTemplates.length;
  const templateNameById = new Map(templates.map((t) => [String(t.id), t.name]));
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [picker, setPicker] = useState(null); // 'template' | 'item' | null
  const [dragIndex, setDragIndex] = useState(null);
  const atCap = documents.length >= maxDocuments;

  const handleFiles = (fileList) => {
    const pdfs = Array.from(fileList || []).filter((f) => f.type === 'application/pdf');
    // Sequential rather than parallel: each upload appends at the end, so
    // firing them together would race and scramble the order the user chose.
    (async () => {
      for (const file of pdfs) {
        if (documents.length + 1 > maxDocuments) break;
        await onUpload(file);
      }
    })();
  };

  const moveTo = (from, to) => {
    if (from === to || from == null || to == null) return;
    const next = documents.map((d) => d.id);
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorder(next);
  };

  return (
    <div className='flex-1 overflow-y-auto'>
      <div className='max-w-2xl w-full mx-auto px-4 py-6'>
        {scopeNote && (
          <div className='flex items-start gap-2.5 px-4 py-3 rounded-lg bg-blue-50 border border-blue-100 mb-5'>
            <Info size={16} className='text-blue-600 flex-shrink-0 mt-0.5' />
            <p className='text-sm text-blue-900'>{scopeNote}</p>
          </div>
        )}

        {documents.length > 0 && (
          <ul className='space-y-2 mb-4'>
            {documents.map((doc, index) => (
              <li
                key={doc.id}
                draggable
                onDragStart={() => setDragIndex(index)}
                onDragEnd={() => setDragIndex(null)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  moveTo(dragIndex, index);
                  setDragIndex(null);
                }}
                className={`flex items-center gap-3 bg-white border rounded-lg px-3 py-2.5 ${
                  dragIndex === index ? 'border-blue-400 opacity-60' : 'border-gray-200'
                }`}
              >
                <span className='text-xs text-gray-400 font-mono w-4 text-center flex-shrink-0'>
                  {index + 1}
                </span>
                <FileText size={17} className='text-gray-400 flex-shrink-0' />
                <div className='flex-1 min-w-0'>
                  <input
                    type='text'
                    value={doc.name}
                    maxLength={255}
                    onChange={(e) => onRename?.(doc.id, e.target.value)}
                    className='w-full text-sm font-medium text-gray-900 bg-transparent focus:outline-none focus:bg-gray-50 rounded px-1 -ml-1'
                  />
                  <div className='flex items-center gap-2 text-xs text-gray-500 px-1 -ml-1'>
                    <span>
                      {doc.pageCount} {doc.pageCount === 1 ? 'page' : 'pages'}
                    </span>
                    {doc.source === 'template' && (
                      <span
                        title={templateChipText(doc, templateNameById)}
                        className='px-1.5 rounded bg-teal-50 text-teal-700 text-[10px] font-medium truncate max-w-[200px]'
                      >
                        {templateChipText(doc, templateNameById)}
                      </span>
                    )}
                    {doc.source === 'file_column' && (
                      <span className='text-gray-400'>from the Files column</span>
                    )}
                  </div>
                </div>
                <span
                  title='Drag to reorder — this is the order signers walk through'
                  className='text-gray-300 cursor-grab flex-shrink-0'
                >
                  <GripVertical size={15} />
                </span>
                <button
                  onClick={() => onRemove(doc.id)}
                  title='Remove — any fields placed on it go too'
                  className='p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0'
                >
                  <Trash2 size={15} />
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* ── Add ── */}
        {documents.length === 0 ? (
          <>
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              handleFiles(e.dataTransfer.files);
            }}
            className={`border-2 border-dashed rounded-lg py-12 text-center bg-white transition-colors ${
              dragOver ? 'border-blue-400 bg-blue-50/40' : 'border-gray-300'
            }`}
          >
            <Upload size={26} className='mx-auto text-blue-600 mb-2' />
            <div className='text-sm font-medium text-gray-800'>Drop a PDF here, or</div>
            <div className='flex items-center justify-center flex-wrap gap-2 mt-3'>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
                className='flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50'
              >
                {/* The spinner stays — it is the only feedback while a PDF
                    uploads. The upload glyph beside "Choose a file" was saying
                    what the label already said. */}
                {busy && <RotateCw size={14} className='animate-spin' />}
                Choose a file
              </button>
              {allowItemFiles && (
                <button
                  onClick={() => setPicker('item')}
                  disabled={busy}
                  className='px-3.5 py-2 rounded-md text-sm font-medium border border-gray-300 text-gray-700 hover:border-gray-400 disabled:opacity-50'
                >
                  From the Files column
                </button>
              )}
            </div>
            <p className='text-xs text-gray-400 mt-3'>PDF only · up to {maxDocuments} documents</p>
          </div>

          {allowTemplates && (
            <TemplateStarters
              published={publishedTemplates}
              unpublished={unpublishedTemplates}
              busy={busy}
              onPick={onAddFromTemplate}
              onManage={onManageTemplates}
              onEdit={onEditTemplate}
              onCreate={onCreateTemplate}
            />
          )}
          </>
        ) : (
          <AddDocumentMenu
            busy={busy}
            atCap={atCap}
            maxDocuments={maxDocuments}
            documentCount={documents.length}
            allowTemplates={allowTemplates}
            allowItemFiles={allowItemFiles}
            publishedCount={publishedTemplates.length}
            itemFiles={itemFiles}
            onUploadClick={() => fileInputRef.current?.click()}
            onTemplateClick={() => setPicker('template')}
            onColumnClick={() => setPicker('item')}
          />
        )}

        <input
          ref={fileInputRef}
          type='file'
          accept='application/pdf'
          multiple
          className='hidden'
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {picker === 'template' && (
        <PickerModal
          title='Add from a template'
          hint='Copies its documents, field placements and roles. Editing it later never changes this request.'
          rows={publishedTemplates.map((t) => ({
            id: t.id,
            label: t.name,
            meta: `${t.documentCount} doc${t.documentCount === 1 ? '' : 's'} · v${t.version}`,
          }))}
          empty={
            unpublishedCount > 0
              ? `You have ${unpublishedCount} unpublished template${
                  unpublishedCount === 1 ? '' : 's'
                }. A template has to be published before a request can start from it.`
              : 'No templates on this board yet. Build one to reuse its documents, field placements and roles.'
          }
          createLabel='Create new template'
          onCreate={
            onCreateTemplate || onManageTemplates
              ? () => {
                  // Closed first: creating one opens the template editor, which
                  // means leaving this request — a modal still standing over
                  // that would be a leftover from a screen that is gone.
                  setPicker(null);
                  (onCreateTemplate || onManageTemplates)();
                }
              : null
          }
          onClose={() => setPicker(null)}
          onPick={async (id) => {
            setPicker(null);
            await onAddFromTemplate(id);
          }}
        />
      )}

      {picker === 'item' && (
        <PickerModal
          title='Add from a File column'
          hint='Every PDF in the column comes across, copied once — later changes to the board files will not follow.'
          rows={(itemFiles || []).map((col) => ({
            id: col.id,
            label: col.title,
            // The count IS the choice: with three File columns on a board, the
            // titles alone rarely say which one holds the contract.
            meta:
              col.pdfCount === 0
                ? 'no PDFs'
                : `${col.pdfCount} PDF${col.pdfCount === 1 ? '' : 's'}`,
            disabled: col.pdfCount === 0,
          }))}
          empty={itemFiles === null ? 'Loading columns…' : 'This board has no File columns.'}
          onClose={() => setPicker(null)}
          onPick={async (id) => {
            setPicker(null);
            const col = (itemFiles || []).find((c) => c.id === id);
            await onAddFromColumn(id, col?.title);
          }}
        />
      )}
    </div>
  );
}

/**
 * "Add document" — one button, three sources behind it.
 *
 * Was three peer buttons in a row. Adding a document is ONE idea and the source
 * is a detail of it, so it gets one control; three peers also read as equally
 * likely when uploading is what almost everyone does. The row of chrome was
 * permanent, and a fourth source would have had nowhere to go.
 *
 * Each row carries its own count, so "From a template" can say there are none
 * published before you open a picker that would only tell you the same thing.
 */
function AddDocumentMenu({
  busy,
  atCap,
  maxDocuments,
  documentCount,
  allowTemplates,
  allowItemFiles,
  publishedCount,
  itemFiles,
  onUploadClick,
  onTemplateClick,
  onColumnClick,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Pointerdown, not click: a click listener would fire on the same event that
    // opened the menu and close it again immediately.
    const onAway = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onAway);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onAway);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Total across every column, so the menu row can say whether opening the
  // picker is worth it at all.
  const pdfCount =
    itemFiles === null ? null : itemFiles.reduce((n, col) => n + (col.pdfCount || 0), 0);
  const slotsLeft = Math.max(0, maxDocuments - documentCount);

  const choose = (fn) => {
    setOpen(false);
    fn();
  };

  return (
    <div className='relative inline-block' ref={wrapRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={busy || atCap}
        aria-haspopup='menu'
        aria-expanded={open}
        title={atCap ? `${maxDocuments}-document limit reached` : 'Add another document'}
        className='flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border border-gray-200 text-gray-700 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed'
      >
        {busy ? <RotateCw size={13} className='animate-spin' /> : <Plus size={14} />}
        Add document
        <ChevronDown size={13} className='text-gray-400' />
      </button>

      {atCap && (
        <span className='ml-2 text-xs text-amber-700'>{maxDocuments}-document limit reached</span>
      )}

      {open && !atCap && (
        <div
          role='menu'
          className='absolute left-0 z-20 mt-1.5 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-1'
        >
          <MenuRow
            icon={Upload}
            title='Upload a PDF'
            detail='From this computer'
            onClick={() => choose(onUploadClick)}
          />
          {allowTemplates && (
            <MenuRow
              icon={Layers}
              title='From a template'
              detail={
                publishedCount === 0
                  ? 'None published yet — open to see your drafts'
                  : `${publishedCount} published on this board`
              }
              // Deliberately NOT disabled at zero, unlike the Files column
              // below: the template picker's empty state names the drafts you
              // have and offers the way to publish one. Disabling the row would
              // hide the only route to that.
              onClick={() => choose(onTemplateClick)}
            />
          )}
          {allowItemFiles && (
            <MenuRow
              icon={FolderOpen}
              title='From the Files column'
              detail={
                pdfCount === null
                  ? 'Checking this item…'
                  : pdfCount === 0
                    ? 'No PDFs in any File column'
                    : `${pdfCount} PDF${pdfCount === 1 ? '' : 's'} across your File columns`
              }
              disabled={pdfCount === 0}
              onClick={() => choose(onColumnClick)}
            />
          )}
          <div className='mt-1 pt-1.5 border-t border-gray-100'>
            <p className='px-2 pb-1 text-[11px] text-gray-400'>
              {slotsLeft} of {maxDocuments} slots left
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function MenuRow({ icon: Icon, title, detail, disabled = false, onClick }) {
  return (
    <button
      role='menuitem'
      disabled={disabled}
      onClick={onClick}
      className={`flex w-full items-start gap-2.5 px-2 py-2 rounded-md text-left ${
        disabled ? 'opacity-45 cursor-not-allowed' : 'hover:bg-gray-50'
      }`}
    >
      <Icon size={14} className='flex-shrink-0 mt-0.5 text-gray-500' />
      <span className='min-w-0'>
        <span className='block text-[13px] font-medium text-gray-900'>{title}</span>
        <span className='block text-[11px] text-gray-400 leading-snug'>{detail}</span>
      </span>
    </button>
  );
}

/**
 * "Or start from a template" — the section under the dropzone.
 *
 * Three states, and the two empty ones carry the weight: a section that only
 * appears when it has cards is the hidden button all over again, so a
 * board whose templates are all drafts says so and offers the way out, and a
 * board with none at all uses the space to explain what a template buys you.
 *
 * Capped rather than scrolled: three cards, then a narrow "+N more" that opens
 * the full list. Three is enough to recognise the one you want, and keeping the
 * count to one row keeps the dropzone above the fold on a short item view.
 */
function TemplateStarters({ published, unpublished, busy, onPick, onManage, onEdit, onCreate }) {
  const shown = published.slice(0, STARTER_LIMIT);
  const hiddenCount = published.length - shown.length;
  const hasOverflow = hiddenCount > 0;
  // Name the draft only when there is exactly one — "finish Mutual NDA" is
  // actionable, "finish one of your 4 drafts" is not.
  const lonelyDraft = unpublished.length === 1 ? unpublished[0] : null;

  return (
    <div className='mt-5'>
      <div className='flex items-center justify-between mb-2'>
        <h4 className='text-[11px] font-semibold uppercase tracking-wider text-gray-500'>
          Or start from a template
        </h4>
        {onManage && published.length > 0 && (
          <button
            onClick={onManage}
            className='flex items-center gap-0.5 text-[11px] font-medium text-blue-600 hover:text-blue-700'
          >
            Manage templates
            <ChevronRight size={11} />
          </button>
        )}
      </div>

      {published.length > 0 ? (
        <>
          <div
            className={`grid grid-cols-1 gap-2 ${
              hasOverflow ? 'sm:grid-cols-[repeat(3,minmax(0,1fr))_auto]' : 'sm:grid-cols-3'
            }`}
          >
            {shown.map((t) => {
              const touched = lastTouchedAt(t);
              return (
                <button
                  key={t.id}
                  onClick={() => onPick(t.id)}
                  disabled={busy}
                  title='Copies its documents, field placements and roles into this request'
                  className='text-left bg-white border border-gray-200 rounded-lg px-3 py-2.5 hover:border-blue-500 disabled:opacity-50 transition-colors'
                >
                  <div className='flex items-center gap-1.5 min-w-0'>
                    <Layers size={13} className='text-gray-400 flex-shrink-0' />
                    <span className='text-sm font-medium text-gray-900 truncate'>{t.name}</span>
                  </div>
                  <div className='text-[11px] text-gray-500 mt-1'>
                    {t.documentCount} doc{t.documentCount === 1 ? '' : 's'} · {t.roleCount} signer
                    {t.roleCount === 1 ? '' : 's'}
                    <span className='ml-1.5 px-1 rounded bg-teal-50 text-teal-700 text-[10px] font-medium'>
                      v{t.version}
                    </span>
                  </div>
                  {touched && (
                    <div className='text-[10px] text-gray-400 mt-0.5'>
                      Updated {updatedText(touched)}
                    </div>
                  )}
                </button>
              );
            })}
            {hasOverflow && (
              <button
                onClick={onManage}
                title={`See all ${published.length} templates`}
                className='flex items-center justify-center whitespace-nowrap bg-white border border-dashed border-gray-300 rounded-lg px-3 py-2.5 text-xs font-medium text-gray-600 hover:border-gray-400'
              >
                +{hiddenCount} more
                <ChevronRight size={12} className='ml-0.5' />
              </button>
            )}
          </div>
        </>
      ) : unpublished.length > 0 ? (
        <div className='flex items-start gap-2.5 bg-white border border-gray-200 rounded-lg px-3.5 py-3'>
          <AlertTriangle size={15} className='text-amber-600 flex-shrink-0 mt-0.5' />
          <div className='flex-1 min-w-0'>
            <p className='text-xs text-gray-600'>
              <span className='font-medium text-gray-800'>
                {unpublished.length === 1
                  ? "1 template isn't published yet."
                  : `${unpublished.length} templates aren't published yet.`}
              </span>{' '}
              A request copies a template&apos;s published version, so drafts can&apos;t be used
              here.
            </p>
            {lonelyDraft && onEdit ? (
              <button
                onClick={() => onEdit(lonelyDraft.id)}
                className='mt-2 flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium border border-gray-200 text-gray-700 hover:border-gray-300'
              >
                Finish &ldquo;{lonelyDraft.name}&rdquo;
                <ChevronRight size={12} />
              </button>
            ) : (
              onManage && (
                <button
                  onClick={onManage}
                  className='mt-2 flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium border border-gray-200 text-gray-700 hover:border-gray-300'
                >
                  Manage templates
                  <ChevronRight size={12} />
                </button>
              )
            )}
          </div>
        </div>
      ) : (
        <div className='flex items-start gap-2.5 bg-white border border-gray-200 rounded-lg px-3.5 py-3'>
          <Layers size={15} className='text-gray-400 flex-shrink-0 mt-0.5' />
          <div className='flex-1'>
            <p className='text-xs text-gray-600'>
              Sending the same paperwork often? Save it once as a template — the PDF, every field
              placement and the signer roles all come back in one click.
            </p>
            {(onCreate || onManage) && (
              <button
                onClick={onCreate || onManage}
                className='mt-2 px-2.5 py-1.5 rounded-md text-xs font-medium border border-gray-200 text-gray-700 hover:border-gray-300'
              >
                Create a template
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * One-choice list in a modal — used for both the template and file pickers.
 *
 * The empty state carries an action where one exists: a picker that only says
 * "nothing here" leaves the user stuck, which is exactly what hiding the whole
 * button used to do.
 *
 * `createLabel`/`onCreate` add a second way out of the POPULATED list, for the
 * case the empty state already covers: none of these is the one you want. Only
 * the template picker passes them — there is no "create a file column" to
 * offer — which is why they are optional rather than assumed.
 */
function PickerModal({
  title,
  hint,
  rows,
  empty,
  createLabel,
  onCreate,
  onClose,
  onPick,
}) {
  const [selected, setSelected] = useState(null);

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4' onClick={onClose}>
      <div
        className='bg-white rounded-xl shadow-xl w-full max-w-sm max-h-[80vh] flex flex-col'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='px-4 py-3 border-b border-gray-100'>
          <div className='text-sm font-semibold text-gray-900'>{title}</div>
        </div>
        <div className='flex-1 overflow-y-auto p-4'>
          {rows.length === 0 ? (
            /* No action here. The footer's "Create new template" is the way
               out of an empty list, and a second, differently-worded button
               six lines above it made the two look like different journeys. */
            <div className='py-6 text-center'>
              <p className='text-sm text-gray-500 max-w-xs mx-auto'>{empty}</p>
            </div>
          ) : (
            <>
              <div className='space-y-1'>
                {rows.map((row) => (
                  /* `row.disabled` is shown, not hidden: an empty File column
                     still answers "is the contract in Files or Files 1?", and a
                     column missing from the list looks like a bug. */
                  <label
                    key={row.id}
                    className={`flex items-center gap-2 px-3 py-2 border rounded-lg ${
                      row.disabled
                        ? 'border-gray-100 opacity-50 cursor-not-allowed'
                        : selected === row.id
                          ? 'border-blue-500 bg-blue-50/50 cursor-pointer'
                          : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                    }`}
                  >
                    <input
                      type='radio'
                      name='picker'
                      disabled={row.disabled}
                      checked={selected === row.id}
                      onChange={() => setSelected(row.id)}
                    />
                    <span className='text-sm text-gray-900 truncate'>{row.label}</span>
                    {row.meta && (
                      <span className='text-[11px] text-gray-400 ml-auto flex-shrink-0'>{row.meta}</span>
                    )}
                  </label>
                ))}
              </div>
              {hint && <p className='text-[11px] text-gray-400 mt-2'>{hint}</p>}
            </>
          )}
        </div>
        <div className='flex justify-end gap-2 px-4 py-3 border-t border-gray-100'>
          <button onClick={onClose} className='px-3 py-1.5 rounded-md text-sm text-gray-600 hover:bg-gray-50'>
            Cancel
          </button>
          {createLabel && onCreate && (
            /* Outlined, not filled: it leaves this request to go and build
               something else, so it must not read as the way to finish here. */
            <button
              onClick={onCreate}
              className='px-3 py-1.5 rounded-md text-sm font-medium text-gray-700 border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            >
              {createLabel}
            </button>
          )}
          <button
            onClick={() => onPick(selected)}
            disabled={!selected}
            className='px-3 py-1.5 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50'
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
}
